import React, { createContext, useState, useEffect, useContext } from 'react'

export const ThemeContext = createContext({})

export const ThemeProvider = ({ children }) => {
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode')
    return saved ? JSON.parse(saved) : false
  })

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode))
    document.body.style.fontFamily = 'Inter, ui-sans-serif, system-ui, sans-serif'
    if (darkMode) {
      document.body.style.backgroundColor = '#0a1628'
    } else {
      document.body.style.backgroundColor = '#eef4fa'
    }
  }, [darkMode])

  const toggleDarkMode = () => {
    setDarkMode(prev => !prev)
  }

  const theme = {
    darkMode,
    toggleDarkMode,

    // Spacing scale - use these instead of arbitrary values
    spacing: {
      xs: '0.25rem',    // 4px
      sm: '0.5rem',     // 8px
      md: '1rem',       // 16px
      lg: '1.5rem',     // 24px
      xl: '2rem',       // 32px
      '2xl': '3rem',    // 48px
      '3xl': '4rem'     // 64px
    },

    // Border radius scale - consistent rounding
    radius: {
      sm: '4px',
      md: '8px',
      lg: '12px',
      xl: '16px',
      full: '9999px'
    },

    // Shadow scale - for depth and elevation
    shadows: {
      sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
      none: 'none'
    },

    // Typography scale
    typography: {
      fontSizes: {
        xs: '0.75rem',      // 12px
        sm: '0.875rem',     // 14px
        base: '1rem',       // 16px
        lg: '1.125rem',     // 18px
        xl: '1.25rem',      // 20px
        '2xl': '1.5rem',    // 24px
        '3xl': '1.875rem',  // 30px
        '4xl': '2.25rem'    // 36px
      },
      fontWeights: {
        normal: 400,
        medium: 500,
        semibold: 600,
        bold: 700
      },
      lineHeights: {
        tight: 1.25,
        normal: 1.5,
        relaxed: 1.75
      }
    },

    // Brand colors (consistent across light/dark)
    brand: {
      primary: '#3d6494',
      primaryHover: '#2d5080',
      success: darkMode ? '#37d67a' : '#10884a',
      successHover: darkMode ? '#2abf68' : '#0a6e3a',
      warning: darkMode ? '#ffbd4a' : '#b46904',
      warningHover: darkMode ? '#f0a832' : '#8f5203',
      danger: darkMode ? '#ff5f6d' : '#c9283c',
      dangerHover: darkMode ? '#e54d5a' : '#a81f30',
      info: darkMode ? '#6da8ff' : '#216fd2',
      infoHover: darkMode ? '#5590e8' : '#1a5cba'
    },

    // Status colors with proper contrast
    status: {
      current: {
        bg: darkMode ? 'rgba(55,214,122,0.12)' : 'rgba(16,136,74,0.1)',
        text: darkMode ? '#37d67a' : '#10884a',
        border: darkMode ? 'rgba(55,214,122,0.3)' : 'rgba(16,136,74,0.3)'
      },
      dueSoon: {
        bg: darkMode ? 'rgba(255,189,74,0.12)' : 'rgba(180,105,4,0.1)',
        text: darkMode ? '#ffbd4a' : '#b46904',
        border: darkMode ? 'rgba(255,189,74,0.3)' : 'rgba(180,105,4,0.3)'
      },
      overdue: {
        bg: darkMode ? 'rgba(255,95,109,0.12)' : 'rgba(201,40,60,0.1)',
        text: darkMode ? '#ff5f6d' : '#c9283c',
        border: darkMode ? 'rgba(255,95,109,0.3)' : 'rgba(201,40,60,0.3)'
      },
      never: {
        bg: darkMode ? 'rgba(95,120,147,0.12)' : 'rgba(120,145,170,0.1)',
        text: darkMode ? '#91a9c2' : '#506b87',
        border: darkMode ? 'rgba(95,120,147,0.3)' : 'rgba(120,145,170,0.3)'
      },
      pending: {
        bg: darkMode ? 'rgba(109,168,255,0.12)' : 'rgba(33,111,210,0.1)',
        text: darkMode ? '#6da8ff' : '#216fd2',
        border: darkMode ? 'rgba(109,168,255,0.3)' : 'rgba(33,111,210,0.3)'
      }
    },

    colors: darkMode ? {
      bg: {
        primary: '#0a1628',
        secondary: '#122036',
        tertiary: '#162540',
        card: '#122036',
        hover: '#1a2a4a'
      },
      text: {
        primary: '#edf5ff',
        secondary: '#91a9c2',
        tertiary: '#5f7893'
      },
      border: {
        primary: 'rgba(125,170,218,0.18)',
        secondary: 'rgba(125,170,218,0.32)'
      },
      input: {
        bg: '#162540',
        border: 'rgba(125,170,218,0.18)',
        focus: '#6da8ff'
      }
    } : {
      bg: {
        primary: '#ffffff',
        secondary: '#f5f9fd',
        tertiary: '#edf4fb',
        card: '#ffffff',
        hover: '#f5f9fd'
      },
      text: {
        primary: '#102033',
        secondary: '#3a5470',
        tertiary: '#506b87'
      },
      border: {
        primary: 'rgba(54,93,130,0.16)',
        secondary: 'rgba(54,93,130,0.28)'
      },
      input: {
        bg: '#f5f9fd',
        border: 'rgba(54,93,130,0.16)',
        focus: '#3d6494'
      }
    }
  }

  return <ThemeContext.Provider value={theme}>{children}</ThemeContext.Provider>
}

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
